// @ts-nocheck
/**
   * Career Position List Component
   * 
   * Displays filtered career locations with pagination.
   * Uses client-side data for filtering and display.
   * 
   * @typedef {Object} CareerPositionListRefs
   * @property {HTMLElement|null} countDisplay - Location count display
   * @property {HTMLElement|null} resultsContainer - Results container
   * @property {HTMLElement|null} loadingIndicator - Loading indicator
   * @property {HTMLElement|null} paginationContainer - Pagination container
   * 
   * @extends {HTMLElement}
   */
  class CareerPositionList extends HTMLElement {
    requiredRefs = ['resultsContainer', 'loadingIndicator', 'paginationContainer'];

  /** @type {Array} */
  #locations = [];

  /** @type {number} */
  #currentPage = 1;

    /**
     * Get refs object for accessing DOM elements
     * @returns {CareerPositionListRefs}
     */
    get refs() {
      return {
        countDisplay: this.querySelector('[ref="countDisplay"]'),
        resultsContainer: this.querySelector('[ref="resultsContainer"]'),
        loadingIndicator: this.querySelector('[ref="loadingIndicator"]'),
        paginationContainer: this.querySelector('[ref="paginationContainer"]')
      };
    }

    connectedCallback() {
      this.#initialize();
    }

    /**
     * Initialize the component
     */
    #initialize() {
      document.addEventListener('data:update', this.#handleDataUpdate.bind(this));
      this.addEventListener('click', e => this.handleApplyClick(e));
    }

    disconnectedCallback() {
      document.removeEventListener('data:update', this.#handleDataUpdate.bind(this));
    }

  /**
   * Handle data update event
   * @param {CustomEvent} event - Data update event
   */
  #handleDataUpdate(event) {
    this.#locations = event.detail.locations || [];
    this.#currentPage = 1;
    this.#renderLocations();
  }

  /**
   * Render locations with pagination
   */
  #renderLocations() {
    if (!this.#locations || this.#locations.length === 0) {
      const noResultsText = this.dataset.noResultsText || this.getAttribute('data-default-no-results') || 'No locations found matching your criteria';
      this.refs.resultsContainer.innerHTML = `<p>${noResultsText}</p>`;
      this.#updateCount(0);
      this.#renderPagination(0);
      return;
    }

    // Sort locations by numeric priority ascending (lower numbers first)
    const sortedLocations = [...this.#locations].sort((a, b) => {
      const ap = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : Number.POSITIVE_INFINITY;
      const bp = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : Number.POSITIVE_INFINITY;
      return ap - bp;
    });

    const total_count = sortedLocations.length;
    const perPage = parseInt(this.dataset.perPage || '10');
    const start = (this.#currentPage - 1) * perPage;
    const end = start + perPage;
    const paginatedLocations = sortedLocations.slice(start, end);

    this.#renderLocationCards(paginatedLocations);
    this.#updateCount(total_count);
    this.#renderPagination(total_count);
  }

  /**
   * Render location cards
   * @param {Array} locations - Array of location objects
   */
  #renderLocationCards(locations) {
    if (locations.length === 0) {
      const noResultsText = this.dataset.noResultsText || this.getAttribute('data-default-no-results') || 'No locations found matching your criteria';
      this.refs.resultsContainer.innerHTML = `<p>${noResultsText}</p>`;
      return;
    }
    
    this.refs.resultsContainer.innerHTML = locations.map(location => `
      <div class="position-list__card" role="listitem">
        <div class="position-list__location-info">
          <div class="position-list__location-name">${location.location_name}</div>
          <div class="position-list__location-address">${location.city}, ${location.province_state}</div>
        </div>
        <button 
          class="link position-list__apply-btn link-style-7" 
          data-location-id="${location.id}" 
          data-location-name="${location.location_name}" 
          data-location-address="${location.city}, ${location.province_state}"
          data-location-email="${location.email || ''}"
          data-positions='${JSON.stringify(location.positions.split(','))}'
          aria-label="${(this.dataset.applyButtonText || this.getAttribute('data-default-apply-text') || 'Apply Now')}"
        >
        <span class="position-list__apply-btn-text">
          ${this.dataset.applyButtonText || this.getAttribute('data-default-apply-text') || 'Apply Now'}
          </span>
          <span class="icon icon-arrow-right-circle" aria-hidden="true">
            <svg width="28" height="28" viewBox="-2 -2 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
              <path d="M14 28C21.732 28 28 21.732 28 14C28 6.26801 21.732 0 14 0C6.26801 0 0 6.26801 0 14C0 21.732 6.26801 28 14 28Z" fill="none" stroke="currentColor" stroke-width="2"/>
              <path d="M18.25 14L11.75 20.5L10.25 19L15.25 14L10.25 9L11.75 7.5L18.25 14Z" fill="currentColor"/>
            </svg>
          </span>
        </button>
      </div>
    `).join('');
  }

  /**
   * Update location count display
   * @param {number} total_count - Total number of locations
   */
  #updateCount(total_count) {
    if (!this.refs.countDisplay) return;
    
    const perPage = parseInt(this.dataset.perPage || '10');
    const start = (this.#currentPage - 1) * perPage + 1;
    const end = Math.min(this.#currentPage * perPage, total_count);

    if (total_count === 0) {
      const noResultsText = this.dataset.noResultsText || this.getAttribute('data-default-no-results') || 'No locations found matching your criteria';
      this.refs.countDisplay.textContent = noResultsText;
    } else {
      const countText = this.getAttribute('data-count-text') || 'Showing {{ start }} - {{ end }} of {{ total }} locations';
      const countMessage = countText
        .replace('{{ start }}', start)
        .replace('{{ end }}', end)
        .replace('{{ total }}', total_count);
      this.refs.countDisplay.textContent = countMessage;
      this.refs.countDisplay.setAttribute('aria-label', countMessage);
    }
  }

  /**
   * Render pagination controls using the new pagination component
   * @param {number} total_count - Total number of locations
   */
  #renderPagination(total_count) {
    const perPage = parseInt(this.dataset.perPage || '10');
    const totalPages = Math.ceil(total_count / perPage);
    
    if (totalPages <= 1) {
      this.refs.paginationContainer.innerHTML = '';
      return;
    }

    // Create pagination component
    this.refs.paginationContainer.innerHTML = `
      <pagination-component 
        page="${this.#currentPage}" 
        totalpage="${totalPages}"
      >
        <ol class="pagination"></ol>
      </pagination-component>
    `;

    // Add event listener for pagination changes
    const paginationComponent = this.refs.paginationContainer.querySelector('pagination-component');
    if (paginationComponent) {
      paginationComponent.addEventListener('pagination:change', (event) => {
        this.#currentPage = event.detail.page;
        this.#renderLocations();
      });
    }
  }

  /**
   * Handle apply button click
   * @param {Event} event - Click event
   */
  handleApplyClick(event) {
    const target = event.target;
    const applyBtn = target.closest('.position-list__apply-btn');
    if (applyBtn && applyBtn.dataset.locationName) {
      let positions = [];
      try {
        positions = JSON.parse(applyBtn.dataset.positions || '[]');
      } catch (error) {
        console.error('Error parsing positions data:', error);
      }
      
      document.dispatchEvent(new CustomEvent('careers:open-form', {
        detail: {
          locationName: applyBtn.dataset.locationName,
          locationAddress: applyBtn.dataset.locationAddress,
          email: applyBtn.dataset.locationEmail || '',
          positions: positions
        }
      }));
    }
  }
}

if (!customElements.get('career-position-list')) {
  customElements.define('career-position-list', CareerPositionList);
}

